/**
 * Results table for 患者情報検索 / Search Patient List.
 * 7 columns per legacy blade. Row click triggers parent callback for navigation.
 */

import React, { useCallback, useMemo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { AppPagination, AppTable, AppTableActionButton, AppTooltip } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import type { SearchPatientListPagination, SearchPatientListRow } from '../types';

interface Props {
  data: SearchPatientListRow[];
  pagination: SearchPatientListPagination | null;
  onPageChange: (page: number) => void;
  onDmEditClick: (patientId: number) => void;
  isLoading: boolean;
  /** IDs of rows currently selected via checkbox. */
  selectedIds: number[];
  /** Called when user toggles row checkboxes or the global selection state. */
  onSelectChange: (ids: number[]) => void;
  /**
   * Fired when the 全選択 header checkbox is ticked. Parent fetches all IDs
   * across pages (the table only has the current page's rows in memory) and
   * resolves with the full ID list to apply.
   */
  onSelectAllPages: () => Promise<number[]>;
}

function renderDmLabel(value: number | null): string {
  if (value === 1) return '可';
  if (value === 0) return '不可';
  return '';
}

const SearchPatientListTable: React.FC<Props> = ({
  data,
  pagination,
  onPageChange,
  onDmEditClick,
  isLoading,
  selectedIds,
  onSelectChange,
  onSelectAllPages,
}) => {
  const rowStart = pagination ? (pagination.current_page - 1) * pagination.per_page : 0;
  const currentPageIds = useMemo(() => data.map(row => row.patient_id), [data]);
  const totalCount = pagination?.total ?? 0;
  const isAllRowsSelected = totalCount > 0 && selectedIds.length >= totalCount;
  const isSomeRowsSelected = selectedIds.length > 0 && !isAllRowsSelected;
  const renderSelectionColumnTitle = useCallback(
    (checkboxNode: React.ReactNode) => {
      if (!React.isValidElement(checkboxNode)) return checkboxNode;

      return React.cloneElement(
        checkboxNode as React.ReactElement<{ checked?: boolean; indeterminate?: boolean }>,
        {
          checked: isAllRowsSelected,
          indeterminate: isSomeRowsSelected,
        }
      );
    },
    [isAllRowsSelected, isSomeRowsSelected]
  );

  const columns: ColumnsType<SearchPatientListRow> = useMemo(
    () => [
      {
        title: 'No',
        key: 'no',
        width: 50,
        align: 'right',
        render: (_: unknown, __: SearchPatientListRow, index: number) => rowStart + index + 1,
      },
      {
        title: '受診者番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 130,
        render: (val: SearchPatientListRow['serial_number'], record: SearchPatientListRow) => (
          <PatientEditLink patientId={record.patient_id} value={val} fallback="" />
        ),
      },
      {
        title: '受診者仮名',
        dataIndex: 'name_kana',
        key: 'name_kana',
        width: 160,
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '受診者漢字名',
        dataIndex: 'name',
        key: 'name',
        width: 180,
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '住所',
        dataIndex: 'address',
        key: 'address',
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '電話番号',
        dataIndex: 'telephone',
        key: 'telephone',
        width: 130,
      },
      {
        title: '続柄',
        dataIndex: 'relationship_name',
        key: 'relationship_name',
        width: 100,
      },
      {
        title: 'DM発送',
        key: 'is_enable_dm',
        width: 100,
        align: 'center',
        render: (_: unknown, record: SearchPatientListRow) => {
          const dmLabel = renderDmLabel(record.is_enable_dm);

          return (
            // Legacy R2 layout: label on the left, [編集] floated right. Empty label still
            // shows the button on the right via justify-between. Button is unconditional.
            <div className="flex items-center justify-center gap-2">
              {dmLabel && <span>{dmLabel}</span>}
              <AppTableActionButton
                action="detail"
                onClick={() => onDmEditClick(record.patient_id)}
              >
                編集
              </AppTableActionButton>
            </div>
          );
        },
      },
    ],
    [rowStart, onDmEditClick]
  );

  const totalCountLabel = `該当件数: ${pagination && pagination.total > 0 ? pagination?.total : 0} 件`;

  return (
    <div>
      <div className="mb-2 text-sm text-gray-700">{totalCountLabel}</div>
      {/*
       * AppTable auto-renders 該当する検索結果がありません。 (when URL has filter params)
       * or データがありません。 (no filters). URL param sync happens in the page.
       * See AppTable.tsx DEFAULT_NON_FILTER_PARAMS for excluded keys (page, per_page, …).
       */}
      <AppTable
        columns={columns}
        dataSource={data}
        rowKey={(record: SearchPatientListRow) => record.patient_id}
        loading={isLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        tableLayout="fixed"
        rowSelection={{
          selectedRowKeys: selectedIds,
          columnTitle: renderSelectionColumnTitle,
          onChange: (keys, _selectedRows, info) => {
            if (info.type === 'all') return;
            onSelectChange(keys.map(k => Number(k)));
          },
          columnWidth: 56,
          align: 'center',
          fixed: true,
          // Keep selections made on earlier pages when navigating to a new page.
          // Without this, antd drops keys that aren't in the current `dataSource`.
          preserveSelectedRowKeys: true,
          // 全選択 header checkbox — spec requires ticking rows across ALL pages,
          // not just the current page. antd's default ticks only visible rows,
          // so we delegate to the parent to fetch the full ID list. Unticking
          // clears every selected row across pages.
          onSelectAll: async (selected: boolean) => {
            if (!selected) {
              onSelectChange([]);
              return;
            }
            try {
              const allIds = await onSelectAllPages();
              onSelectChange(allIds);
            } catch {
              // Parent surfaces its own toast; fall back to current-page selection.
              onSelectChange(Array.from(new Set([...selectedIds, ...currentPageIds])));
            }
          },
        }}
      />

      {pagination && pagination.total > 0 ? (
        <div className="flex justify-center mt-3">
          <AppPagination
            current={pagination.current_page}
            total={pagination.total}
            pageSize={pagination.per_page}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      ) : null}
    </div>
  );
};

export default SearchPatientListTable;
